import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ConsentTypes } from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import {
  ConsentTextInfoQueryService,
  DealerLocation,
  monthlyPayment,
  portalApplicationId,
  ResidenceInfo,
  SubmitCapsLeadMutationService,
} from 'graphql-client';
import { catchError, map, takeUntil, tap } from 'rxjs';

import { AWSService } from '../services/aws.service';
import { PrequalService } from '../services/prequal.service';
import { ScrollingService } from '../services/scrolling.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS, FS_LABELS } from '../shared/constants/fsevents';

declare const FS: typeof FullStory;

@Component({
  selector: 'capq-success-prequal',
  templateUrl: './success-prequal.component.html',
  styleUrls: ['./success-prequal.component.scss'],
})
export class SuccessPrequalComponent
  extends Unsubscribe
  implements OnInit, AfterViewInit
{
  @Input() immediateEmit = false;
  @Input() isPrequal = false;
  @Input() openLoanError = false;
  @Input() adobeScriptLoaded = false;
  @Input() submissionFailureReason = '';

  @ViewChild('topElement') topElement!: ElementRef<HTMLDivElement>;

  zipCode = 48034;
  models: string[] = [];
  monthlyPayment = 0;
  logoURL = 'assets/images/CA_Logo.svg';
  prequalImg = 'assets/images/pre-qual.png';
  dealers!: DealerLocation[];
  osmSSNConsentContent = '';
  isUserAgreed = false;
  desktopView: boolean | undefined;
  finalResponse = false;
  saveMeCounter = 60;
  formFieldData: any;
  consentCreditAuthVersion: any;
  consentversionid = '';
  globalText: any;
  timeIntervalId: any;
  ttMETAstarttime: number | null = null;
  isWaiting = false;
  residenceData: any = {};

  constructor(
    private prequalService: PrequalService,
    private awsService: AWSService,
    private consentTextInfoQueryService: ConsentTextInfoQueryService,
    private readonly submitCapsLeadMutationService: SubmitCapsLeadMutationService,
    private readonly scrollingService: ScrollingService
  ) {
    super();
    this.monthlyPayment = monthlyPayment();
  }

  ngOnInit(): void {
    this.ttMETAstarttime = new Date().getTime();
    this.awsService.prequalifiedJSONData$
      .pipe(
        tap((data) => {
          if (data) {
            this.formFieldData = data.success;
          }
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.dealers = this.prequalService.getDealerLocations() ?? [];
    this.residenceData = this.prequalService.getResidenceData() ?? {};
    this.awsService.prequalifiedJSONData$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        if (data) {
          this.formFieldData = data.success;
        }
      });

    //Get the SSN consent and display in page
    this.consentTextInfoQueryService
      .fetch({
        consentType: ConsentTypes.OSM_SSN,
      })
      .pipe(
        map((result) => result.data.getConsentByType),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data) => {
        this.osmSSNConsentContent = data.consenttext;
        this.consentCreditAuthVersion = data.consenttextid;
        this.consentversionid = data.consentversion;
      });

    setTimeout(() => {
      this.startTimer();
    }, 180000);

    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-page-view', null, {
        form_design: 'v 1.0',
        section_name: 'prequalified',
        form_submission_id: (portalApplicationId() || '').toString(),
        dealer_list: '',
        form_submission_status: this.openLoanError ? 'failure' : 'success',
        form_failure_reason: this.submissionFailureReason
          ? this.submissionFailureReason
          : undefined,
        event_type: 'cac-page-view',
      });
  }

  ngAfterViewInit(): void {
    this.scrollingService.scrollToTop(this.topElement.nativeElement);
  }

  startTimer() {
    this.timeIntervalId = setInterval(() => {
      this.saveMeCounter = this.saveMeCounter - 1;
      if (this.saveMeCounter == 0) {
        clearInterval(this.saveMeCounter);
        document.location.href = document.location.origin;
      }
    }, 1000);
  }

  stopTimer() {
    clearInterval(this.timeIntervalId);
  }

  handleSubmit() {
    this.isWaiting = true;
    let dealer_list = '';
    this.dealers.forEach((dealer, ind) => {
      dealer_list += `${dealer.dealerId}|${dealer.dealerName}`;
      if (ind < 2) {
        dealer_list += ', ';
      }
    });
    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-save-me-time', null, {
        form_design: 'v 1.0',
        section_name: '',
        form_submission_id: '',
        dealer_list,
        form_submission_status: 'success',
        event_type: 'cac-save-me-time',
      });

    this.submitCapsLeadMutationService
      .mutate({
        agreementConsent: this.isUserAgreed,
      })
      .pipe(
        map(() => {
          this.isWaiting = false;
        }),
        catchError(() => {
          this.prequalService.triggerErrorScreen(true);
          throw new Error();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.OSM_HOT_LEAD, {
        action_label: FS_LABELS.SAVE_ME_TIME,
        osmHotLeadSubmitted: true,
      });
    }

    this.finalResponse = true;
    this.stopTimer();
  }

  onCheckboxChange(e: any) {
    if (e.target.checked) {
      this.isUserAgreed = true;
    } else {
      this.isUserAgreed = false;
    }
  }
}
